import React from 'react';
import { Form, useFormikContext } from 'formik';

import type { Dictionary } from 'app/utils';
import { isNil } from 'app/utils';

import { RegulationsFieldInput, RegulationsFieldDisplay } from '../../Fields';

import type {
  EmployerRegulationsScalesTableColumn,
  MaximumFiscalPercentagesSelectItem,
  RegulationsEmployerScalesFormValues,
} from '../RegulationsEmployerScales.types';

import {
  ActionButtonStyled,
  DivButtonsContainerStyled,
  DivTableContainerStyled,
} from './RegulationsEmployerScalesTable.styles';
import { IntlMessage } from 'app/components/Intl';
import { FilterableTable, HasAccessTo } from 'app/components';
import { EMPLOYER } from 'app/common/Authorization/entities';
import { EDIT } from 'app/common/Authorization/permissions';

enum RowTypeEnum {
  BasicPensionRegulations = 'basicPensionScheme',
  wtp = 'wtp',
  MaximumFiscalPercentage = 'maximumFiscalPercentage',
  Diff = 'diff',
}

type RowData = {
  data: {
    type: RowTypeEnum;
    enabled?: boolean;
  };
};

type RegulationsEmployerScalesTableProps = {
  readonly: boolean;
  toggleReadonly: () => void;
  loading: boolean;
  year: number;
  ages: Array<string>;
  columns: Array<Array<EmployerRegulationsScalesTableColumn>>;
  data: {
    basicPensionRegulation: Dictionary<number>;
    wtp: Dictionary<number>;
  };
  maximumFiscalPercentagesSelectItems: Array<MaximumFiscalPercentagesSelectItem>;
};

const percentageFieldFormat = {
  type: 'number',
  options: { style: 'percent', minimumFractionDigits: 2 },
};

export const RegulationsEmployerScalesTable = ({
  readonly,
  toggleReadonly,
  loading,
  year,
  ages,
  columns,
  data,
  maximumFiscalPercentagesSelectItems,
}: RegulationsEmployerScalesTableProps) => {
  const hasEditScalesRole = HasAccessTo(EMPLOYER, EDIT);
  const { values, setValues, resetForm, isSubmitting, submitForm, isValid } =
    useFormikContext<RegulationsEmployerScalesFormValues>();

  const onClickCancelButton = React.useCallback(() => {
    toggleReadonly();
    resetForm();
  }, [resetForm, toggleReadonly]);

  const handleNumberFieldChange = React.useCallback((value: any) => (value === '' ? 0 : value), []);

  const isOverMaximum = React.useMemo(() => {
    const maxValues = values.selectedOption?.values?.agePercentages || {};
    return ages.some(age => {
      const maxValue = maxValues[age];
      const basicPensionValue = values.basicPensionScheme[age as keyof typeof values];
      return !isNil(maxValue) && !isNil(basicPensionValue) && basicPensionValue > maxValue;
    });
  }, [ages, values.basicPensionScheme, values.selectedOption]);

  const rows = React.useMemo<Array<RowData>>(
    () =>
      [
        {
          data: {
            type: RowTypeEnum.BasicPensionRegulations,
          },
        },
        {
          data: {
            type: RowTypeEnum.wtp,
            enabled: year > 2023,
          },
        },
        {
          data: {
            type: RowTypeEnum.MaximumFiscalPercentage,
          },
        },
        {
          data: {
            type: RowTypeEnum.Diff,
          },
        },
      ].filter(row => row.data.enabled || row.data.enabled === undefined),
    [year],
  );

  const onChangeSelectValue = React.useCallback(
    (value: MaximumFiscalPercentagesSelectItem['value']) => {
      const newInputValues = ages.reduce<Dictionary<number>>((acc, age) => ({ ...acc, [age]: 0 }), {});
      setValues(
        {
          selectedOption: value,
          wtp: values.wtp,
          basicPensionScheme: values.basicPensionScheme,
          ...newInputValues,
        },
        true,
      );
    },
    [ages, setValues, values.basicPensionScheme, values.wtp],
  );

  const MaximumFiscalPercentagesSelect = React.useMemo(() => {
    if (readonly) {
      return values.selectedOption?.element;
    }

    return (
      <RegulationsFieldInput
        onChange={onChangeSelectValue}
        field={{
          type: 'autocomplete',
          name: 'selectedOption',
          value: values.selectedOption,
          items: maximumFiscalPercentagesSelectItems,
        }}
      />
    );
  }, [readonly, maximumFiscalPercentagesSelectItems, onChangeSelectValue, values.selectedOption]);

  const percentInputOptions = React.useMemo(
    () => ({
      style: 'percent' as const,
      size: 'small' as const,
      textAlign: 'right' as const,
      maximumFractionDigits: 5,
      minimumFractionDigits: 2,
      min: 0.0,
    }),
    [],
  );

  const editableBasicPensionRegulationsRowValues = React.useMemo<Dictionary<React.ReactElement>>(() => {
    return ages.reduce<Dictionary<React.ReactElement>>((acc, age) => {
      const key = `${RowTypeEnum.BasicPensionRegulations}.${age}`;
      acc[age] = (
        <RegulationsFieldInput
          key={key}
          onChange={handleNumberFieldChange}
          field={{
            type: 'number',
            name: key,
            fullWidth: false,
            options: percentInputOptions,
            defaultValue: data.basicPensionRegulation[age],
          }}
        />
      );
      return acc;
    }, {});
  }, [ages, data.basicPensionRegulation, handleNumberFieldChange, percentInputOptions]);

  const readonlyBasicPensionRegulationsRowValues = React.useMemo<Dictionary<React.ReactElement>>(() => {
    return ages.reduce<Dictionary<React.ReactElement>>((acc, age) => {
      const key = `${RowTypeEnum.BasicPensionRegulations}.${age}`;
      acc[age] = (
        <RegulationsFieldDisplay
          key={`${key}-readonly`}
          field={{
            type: 'number',
            name: key,
            options: percentInputOptions,
            value: values.basicPensionScheme[age as keyof typeof values],
          }}
        />
      );
      return acc;
    }, {});
  }, [ages, percentInputOptions, values.basicPensionScheme]);

  const getBasicPensionRegulationsRowValues = React.useCallback(
    () => (readonly ? readonlyBasicPensionRegulationsRowValues : editableBasicPensionRegulationsRowValues),
    [readonly, readonlyBasicPensionRegulationsRowValues, editableBasicPensionRegulationsRowValues],
  );

  const editableWTPRowValues = React.useMemo<Dictionary<React.ReactElement>>(() => {
    return ages.reduce<Dictionary<React.ReactElement>>((acc, age) => {
      const key = `${RowTypeEnum.wtp}.${age}`;
      acc[age] = (
        <RegulationsFieldInput
          key={key}
          onChange={handleNumberFieldChange}
          field={{
            type: 'number',
            name: key,
            fullWidth: false,
            options: percentInputOptions,
            defaultValue: data.wtp[age],
          }}
        />
      );
      return acc;
    }, {});
  }, [ages, data.wtp, handleNumberFieldChange, percentInputOptions]);

  const readonlyWTPRowValues = React.useMemo<Dictionary<React.ReactElement>>(() => {
    return ages.reduce<Dictionary<React.ReactElement>>((acc, age) => {
      const key = `${RowTypeEnum.wtp}.${age}`;
      acc[age] = (
        <RegulationsFieldDisplay
          key={`${key}-readonly`}
          field={{
            type: 'number',
            name: key,
            options: percentInputOptions,
            value: values.wtp[age as keyof typeof values],
          }}
        />
      );
      return acc;
    }, {});
  }, [ages, percentInputOptions, values.wtp]);

  const getWTPRowValues = React.useCallback(
    () => (readonly ? readonlyWTPRowValues : editableWTPRowValues),
    [readonly, readonlyWTPRowValues, editableWTPRowValues],
  );

  const getMaximumFiscalPercentageRowValues = React.useCallback(() => {
    const rowValues = values.selectedOption?.values?.agePercentages || {};
    return ages.reduce<Dictionary<React.ReactElement>>((acc, age) => {
      const value = rowValues[age] ?? 0;
      acc[age] = (
        <RegulationsFieldDisplay
          key={`${RowTypeEnum.MaximumFiscalPercentage}-${age}`}
          field={{ value, name: age, ...percentageFieldFormat }}
        />
      );
      return acc;
    }, {});
  }, [ages, values.selectedOption?.values?.agePercentages]);

  const getDiffRowValues = React.useCallback(() => {
    return ages.reduce<Dictionary<React.ReactElement>>((acc, age) => {
      const maxValue = values.selectedOption?.values?.agePercentages[age] ?? 0;
      const savingsCalculation =
        values.wtp[age as keyof typeof values] < 3
          ? maxValue - values.basicPensionScheme[age as keyof typeof values]
          : maxValue -
            (values.wtp[age as keyof typeof values] - 3 + values.basicPensionScheme[age as keyof typeof values]);
      const value = Math.max(0, savingsCalculation);
      acc[age] = (
        <RegulationsFieldDisplay
          key={`${RowTypeEnum.Diff}-${age}`}
          field={{ value, name: age, ...percentageFieldFormat }}
        />
      );

      return acc;
    }, {});
  }, [ages, values]);

  const rowMapper = React.useCallback(
    (row: RowData) => {
      const type = row.data.type;

      if (type === RowTypeEnum.BasicPensionRegulations) {
        return {
          data: {
            label: <IntlMessage value="summaryOfRegulations.scales.basicPensionRegulations" />,
            ...getBasicPensionRegulationsRowValues(),
          },
        };
      }

      if (type === RowTypeEnum.wtp) {
        return {
          data: {
            label: <IntlMessage value="summaryOfRegulations.scales.wtp" />,
            ...getWTPRowValues(),
          },
        };
      }
      if (type === RowTypeEnum.MaximumFiscalPercentage) {
        return {
          data: {
            label: readonly ? (
              <IntlMessage value={MaximumFiscalPercentagesSelect.toString()} />
            ) : (
              MaximumFiscalPercentagesSelect
            ),
            ...getMaximumFiscalPercentageRowValues(),
          },
        };
      }

      return {
        data: {
          label: <IntlMessage value="summaryOfRegulations.scales.difference" />,
          ...getDiffRowValues(),
        },
      };
    },
    [
      getDiffRowValues,
      getBasicPensionRegulationsRowValues,
      getWTPRowValues,
      readonly,
      MaximumFiscalPercentagesSelect,
      getMaximumFiscalPercentageRowValues,
    ],
  );

  const disabledIfNoAccessToEdit = HasAccessTo(EMPLOYER, EDIT);

  return (
    <Form>
      <DivTableContainerStyled>
        <FilterableTable
          columns={columns[1]}
          loading={loading}
          rows={rows}
          rowMapper={rowMapper}
          defaultPageSize={3}
          pagination={{ enabled: false }}
        />
      </DivTableContainerStyled>
      <DivButtonsContainerStyled>
        {readonly && disabledIfNoAccessToEdit && (
          <ActionButtonStyled onClick={toggleReadonly} messageId={'button.edit'} disabled={!hasEditScalesRole} />
        )}
        {!readonly && (
          <>
            <ActionButtonStyled
              onClick={onClickCancelButton}
              messageId={'button.cancel'}
              disabled={isSubmitting}
              variant={'outlined'}
            />
            <ActionButtonStyled
              onClick={submitForm}
              messageId={'button.save'}
              disabled={isSubmitting || !isValid || isOverMaximum}
            />
          </>
        )}
      </DivButtonsContainerStyled>
    </Form>
  );
};
